import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { MatchedLocation, MatchedRequirement, MatchedView, MergeSuggestion } from '../lib/api.js';
import { useRefusal } from '../lib/permissions.js';
import { POINTED_AT_STYLE, useScrollIntoViewWhen } from '../lib/useScrollIntoViewWhen.js';
import '../styles/locations.css';
import { LocationMap } from './LocationMap.js';
import { LocationPhoto, locationMetaLine } from './LocationCard.js';
import { LocationPack } from './LocationPack.js';
import { LocationDetailPanel } from './LocationDetailPanel.js';
import { ConversationEntry } from './conversations/ConversationEntry.js';
import { Icon } from './Icon.js';
import { Disclosure } from './ui/Disclosure.js';
import { RejectWithReason } from './RejectWithReason.js';
import { EmptyState } from './ui/EmptyState.js';
import { SegmentedFilter } from './ui/SegmentedFilter.js';
import { SplitWorkspace, navigatorKeyDown } from './ui/SplitWorkspace.js';
import { StatusDot } from './ui/StatusPill.js';

type Filter = 'ALL' | 'SHORTLIST' | 'UNMATCHED' | 'REQUIREMENTS';
type Mode = 'LIST' | 'MAP';

const AXIS_ORDER = ['creative', 'operational', 'schedule', 'geographic', 'budget'] as const;

/** The axes in words a producer uses. `geographic` is "where", which reads as the area. */
const AXIS_LABEL: Readonly<Record<(typeof AXIS_ORDER)[number], string>> = {
  creative: 'Creative',
  operational: 'Operational',
  schedule: 'Schedule',
  geographic: 'Area',
  budget: 'Budget',
};

/**
 * HOW MANY ROWS SHOW BEFORE "SHOW MORE". Fifty matches drawn at once were a
 * 40 000-pixel page; ten compact rows are a screen and a half, and the rest is
 * one click away rather than one long scroll away.
 */
export const RESULTS_PAGE = 10;

/**
 * SHORTLISTED FIRST (2026-10-30). The server orders the places by how many
 * requirements they match, and among equals by no stated order at all, so with
 * ten to a page a place could leave the first page the moment it was
 * shortlisted — on stage the button turned into nothing. A shortlisted place
 * now leads the list and keeps its badge in view; the rest keep the server's
 * order (a stable partition, nothing re-sorted), and rejected places stay
 * apart at the bottom.
 */
export function shortlistedFirst<T>(rows: readonly T[], isShortlisted: (row: T) => boolean): T[] {
  return [...rows.filter(isShortlisted), ...rows.filter((row) => !isShortlisted(row))];
}

/** The navigator's "everything" row. */
const ALL_REQUIREMENTS = '__all__';

/**
 * Every matched location, once.
 *
 * "Each location appears once, however many scenes it serves" is the claim the
 * screen makes, and it is an inversion of how matching works underneath — the
 * engine runs per requirement, so one hotel can be a candidate for three of
 * them. The server does the aggregation so the claim stays true when a
 * requirement is off-screen.
 *
 * THE NUMBER IS COVERAGE, NOT FIT. It says how many of a requirement's
 * must-haves this listing evidences — a count with a denominator the producer
 * can see. It is deliberately NOT a blended score: the frozen matching spec
 * forbids one, and a single figure would average away the one axis that
 * actually disqualifies a place. The five axis states sit beside it and carry
 * the reasoning, which is the part worth reading.
 *
 * NOT ASSESSED IS ITS OWN STATE. A location a producer added by hand has no
 * verdicts at all. Rendering that as "0 of 0" would read as a failed
 * assessment; it is an absent one, and the difference decides whether they go
 * looking for a problem that does not exist.
 *
 * THE LAYOUT (2026-10-30): the requirements on the left, narrowing the results
 * on the right; compact rows with a 4:3 picture instead of a full-width
 * photograph per match; "why it matches" and the scenes folded under each row;
 * ten at a time; rejected places folded at the end. Nothing was removed — the
 * shortlist, the map, the unmatched brief, the possible duplicates and the
 * location pack are all still here.
 */
export function MatchedLocations({
  view,
  projectId,
  mergeSuggestions = [],
  twinRequired = false,
  projectTitle = 'This production',
  scriptVersionLabel = '',
  busy = false,
  primaryAllowed = true,
  onShortlist,
  onReject,
  onRestore,
  onAssignScenes,
  onMerge,
  onRequestScouting,
  onPackPrinted,
  focusPlaceId = null,
}: {
  view: MatchedView;
  /** Enables the per-place conversation entry and the photos & details panel. */
  projectId?: string;
  mergeSuggestions?: readonly MergeSuggestion[];
  /** Shown when it is the reason nothing came back. */
  twinRequired?: boolean;
  projectTitle?: string;
  scriptVersionLabel?: string;
  busy?: boolean;
  /**
   * Whether this section may draw the page's red button. False while the search
   * criteria are open above it: their "Search locations" is the one primary then.
   */
  primaryAllowed?: boolean;
  /** The version is the candidate's own, never a literal — every candidate
   *  write is compare-and-swap and a stale number is a 409 the producer cannot
   *  act on. */
  onShortlist?: (candidateId: string, version: number) => void;
  onReject?: (candidateId: string, version: number, reason: string) => void;
  /** A rejected place back to the candidates (REJECTED → LONGLIST). */
  onRestore?: (candidateId: string, version: number) => void;
  /** Offered only when wired. No API verb assigns a candidate to scenes today. */
  onAssignScenes?: (location: MatchedLocation) => void;
  onMerge?: (requirementIds: readonly string[]) => void;
  onRequestScouting?: (requirementIds: readonly string[], note: string) => void;
  /** The location pack was sent to the print dialog, with this many locations in it. */
  onPackPrinted?: (locationCount: number) => void;
  /** A place a link pointed at (`?place=`): scrolled to and outlined. */
  focusPlaceId?: number | null;
}) {
  const [filter, setFilter] = useState<Filter>('ALL');
  const [mode, setMode] = useState<Mode>('LIST');
  const [requirementId, setRequirementId] = useState<string>(ALL_REQUIREMENTS);
  /** Narrow screens: the requirement list (false) or the results (true). */
  const [showResults, setShowResults] = useState(true);
  const [selectedUnmatched, setSelectedUnmatched] = useState<string[]>([]);
  /** What the script does not say. Travels with the request as its body. */
  const [scoutingNote, setScoutingNote] = useState('');
  const [packOpen, setPackOpen] = useState(false);
  const [limit, setLimit] = useState(RESULTS_PAGE);
  /** The place whose photos & details are open, by its source — or null. */
  const [detailKey, setDetailKey] = useState<string | null>(null);

  const shortlistRefusal = useRefusal('CANDIDATE_EDIT');
  const rejectRefusal = useRefusal('CANDIDATE_REJECT');

  const shortlisted = useMemo(
    () => view.locations.filter((l) => l.requirements.some((r) => r.candidateStatus === 'SHORTLIST')),
    [view.locations],
  );

  const navigator = useMemo(() => requirementsOf(view), [view]);

  // A row is a (location, the requirement it is shown against). Against "all"
  // that is the location's first requirement, as every card always used.
  const inScope = useMemo(() => {
    const rows: { location: MatchedLocation; hit: MatchedRequirement | undefined }[] = [];
    for (const location of view.locations) {
      if (requirementId === ALL_REQUIREMENTS) {
        rows.push({ location, hit: location.requirements[0] });
        continue;
      }
      const hit = location.requirements.find((r) => r.requirementId === requirementId);
      if (hit !== undefined) rows.push({ location, hit });
    }
    return rows;
  }, [view.locations, requirementId]);

  const isShortlisted = (row: (typeof inScope)[number]) =>
    requirementId === ALL_REQUIREMENTS
      ? row.location.requirements.some((r) => r.candidateStatus === 'SHORTLIST')
      : row.hit?.candidateStatus === 'SHORTLIST';

  const scoped = filter === 'SHORTLIST' ? inScope.filter(isShortlisted) : inScope;
  const open = shortlistedFirst(
    scoped.filter((row) => row.hit?.candidateStatus !== 'REJECTED'),
    isShortlisted,
  );
  const rejected = scoped.filter((row) => row.hit?.candidateStatus === 'REJECTED');

  // A place a link pointed at is shown, whatever page it falls on.
  const focusIndex = focusPlaceId === null ? -1 : open.findIndex((row) => row.location.sourceId === focusPlaceId);
  const shownLimit = Math.max(limit, focusIndex + 1);
  const page = open.slice(0, shownLimit);

  // A different question starts from the first page.
  useEffect(() => {
    setLimit(RESULTS_PAGE);
  }, [filter, requirementId]);

  const detail =
    detailKey === null ? undefined : inScope.find((row) => keyOf(row.location) === detailKey && row.hit !== undefined);

  /**
   * THE SECTION'S ONE RED BUTTON, by what is true now: a shortlist to send is
   * the deliverable; with nothing shortlisted, requirements nobody has a
   * candidate for are the gap to close. With neither, the rows' own Shortlist
   * is the work and nothing up here is red. The open criteria form, and the
   * unmatched view's own request, take it over.
   */
  const headerPrimary: 'PACK' | 'UNMATCHED' | null =
    !primaryAllowed || filter === 'UNMATCHED'
      ? null
      : shortlisted.length > 0
        ? 'PACK'
        : view.unmatched.length > 0
          ? 'UNMATCHED'
          : null;

  const selectRequirement = (id: string) => {
    setRequirementId(id);
    setShowResults(true);
    // An unmatched requirement has nothing to list: it opens the brief with it ticked.
    const unmatched = view.unmatched.find((u) => u.id === id);
    if (unmatched !== undefined) {
      setFilter('UNMATCHED');
      setSelectedUnmatched((s) => (s.includes(id) ? s : [...s, id]));
    } else if (filter === 'UNMATCHED' || filter === 'REQUIREMENTS') {
      setFilter('ALL');
    }
  };

  const navIds = [ALL_REQUIREMENTS, ...navigator.map((r) => r.id)];

  const nav = (
    <>
      <div className="ui-nav-head">
        <p className="loc-nav-title">Requirements</p>
      </div>
      <ul
        className="ui-nav-list"
        data-testid="matched-requirements"
        onKeyDown={navigatorKeyDown(navIds, requirementId, selectRequirement, (id) => `[data-nav-id="${id}"]`)}
      >
        <li>
          <button
            type="button"
            className="ui-row"
            data-nav-id={ALL_REQUIREMENTS}
            data-testid="matched-req-ALL"
            aria-current={requirementId === ALL_REQUIREMENTS}
            onClick={() => {
              selectRequirement(ALL_REQUIREMENTS);
            }}
          >
            <Icon name="list" size={15} />
            <span className="ui-row-main">
              <span className="ui-row-title">All requirements</span>
            </span>
            <span className="loc-nav-count">{String(view.locations.length)}</span>
          </button>
        </li>
        {navigator.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              className="ui-row"
              data-nav-id={r.id}
              data-testid={`matched-req-${r.id}`}
              aria-current={requirementId === r.id}
              onClick={() => {
                selectRequirement(r.id);
              }}
            >
              <StatusDot
                state={r.locations === 0 ? 'NEEDS_ATTENTION' : r.shortlisted > 0 ? 'CONFIRMED' : 'READY_FOR_REVIEW'}
                label={r.locations === 0 ? 'No candidate yet' : r.shortlisted > 0 ? 'Has a shortlist' : 'Candidates to review'}
              />
              <span className="ui-row-main">
                <span className="ui-row-title">{r.title}</span>
                <span className="ui-row-sub">
                  {r.locations === 0
                    ? 'No candidate yet'
                    : r.shortlisted > 0
                      ? `${String(r.shortlisted)} shortlisted`
                      : 'None shortlisted yet'}
                </span>
              </span>
              <span className="loc-nav-count">{String(r.locations)}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="ui-nav-foot">Each location appears once, however many scenes it serves.</p>
    </>
  );

  const results = (
    <div data-testid="matched-results">
      <div className="loc-toolbar">
        <SegmentedFilter<Filter>
          label="Show"
          testIdPrefix="matched-tab"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'ALL', label: 'All matched', count: inScope.length },
            { id: 'SHORTLIST', label: 'Shortlisted', count: inScope.filter(isShortlisted).length },
            {
              id: 'UNMATCHED',
              label: 'Unmatched',
              count: view.unmatched.length,
              ...(view.unmatched.length > 0 ? { tone: 'attention' as const } : {}),
            },
            { id: 'REQUIREMENTS', label: 'Possible duplicates', count: mergeSuggestions.length },
          ]}
        />
        {filter === 'ALL' || filter === 'SHORTLIST' ? (
          <div className="loc-toolbar-end">
            <div className="loc-toggle" role="group" aria-label="Show the locations as">
              <button
                type="button"
                data-testid="matched-view-list"
                aria-pressed={mode === 'LIST'}
                onClick={() => {
                  setMode('LIST');
                }}
              >
                <Icon name="list" size={14} />
                List
              </button>
              <button
                type="button"
                data-testid="matched-tab-MAP"
                aria-pressed={mode === 'MAP'}
                onClick={() => {
                  setMode('MAP');
                }}
              >
                <Icon name="map" size={14} />
                Map
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {filter === 'REQUIREMENTS' ? <MergePanel suggestions={mergeSuggestions} busy={busy} onMerge={onMerge} /> : null}

      {filter === 'UNMATCHED' ? (
        <UnmatchedPanel
          unmatched={view.unmatched}
          selected={selectedUnmatched}
          busy={busy}
          primary={primaryAllowed}
          onToggle={(id) =>
            setSelectedUnmatched((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
          }
          note={scoutingNote}
          onNote={setScoutingNote}
          onRequest={() => {
            onRequestScouting?.(selectedUnmatched, scoutingNote.trim());
          }}
        />
      ) : null}

      {(filter === 'ALL' || filter === 'SHORTLIST') && mode === 'MAP' ? (
        <LocationMap
          locations={open.map((row) => row.location)}
          onFocus={(location) => {
            if (projectId === undefined) {
              setMode('LIST');
              return;
            }
            setDetailKey(keyOf(location));
          }}
        />
      ) : null}

      {(filter === 'ALL' || filter === 'SHORTLIST') && mode === 'LIST' ? (
        scoped.length === 0 ? (
          <EmptyState
            testId="matched-empty"
            title={
              filter === 'SHORTLIST'
                ? 'Nothing shortlisted yet.'
                : twinRequired
                  ? 'Nothing matched.'
                  : 'Nothing matched yet.'
            }
          >
            {filter === 'SHORTLIST'
              ? 'Shortlist locations from All matched — your team sees the shortlist instantly.'
              : twinRequired
                ? // NOT "we found nothing like this". The producer asked for a
                  // twin and none exist yet; saying so is the difference between
                  // a dead end and a filter they can lift.
                  'You asked for locations with an AI digital twin, and none have been built yet. Set AI digital twin back to “Any” to see the rest.'
                : 'Set your search criteria and run matching, or add a location by hand from Search the catalogue.'}
          </EmptyState>
        ) : (
          <>
            <ul className="loc-rows">
              {page.map((row) => (
                <MatchedRow
                  key={keyOf(row.location)}
                  projectId={projectId}
                  location={row.location}
                  hit={row.hit}
                  showAllRequirements={requirementId === ALL_REQUIREMENTS}
                  focused={focusPlaceId === row.location.sourceId}
                  busy={busy}
                  shortlistRefusal={shortlistRefusal}
                  rejectRefusal={rejectRefusal}
                  onShortlist={onShortlist}
                  onReject={onReject}
                  onRestore={onRestore}
                  onAssignScenes={onAssignScenes}
                  onDetails={
                    projectId === undefined
                      ? undefined
                      : () => {
                          setDetailKey(keyOf(row.location));
                        }
                  }
                />
              ))}
            </ul>
            {open.length > page.length ? (
              <div className="loc-more">
                <button
                  type="button"
                  className="ctl"
                  data-testid="matched-show-more"
                  onClick={() => {
                    setLimit(shownLimit + RESULTS_PAGE);
                  }}
                >
                  {`Show ${String(Math.min(RESULTS_PAGE, open.length - page.length))} more of ${String(open.length - page.length)}`}
                </button>
              </div>
            ) : null}
            {/* REJECTED GOES TO THE BOTTOM, GREYED (owner, 2026-10-30): still
                in view with why it was ruled out, never in the way of the ones
                still in the running; Restore puts it back. Nothing is deleted. */}
            {rejected.length > 0 ? (
              <div className="loc-rejected" data-testid="matched-rejected-group">
                <p className="loc-rejected-label">{`Rejected (${String(rejected.length)})`}</p>
                <ul className="loc-rows">
                  {rejected.map((row) => (
                    <MatchedRow
                      key={keyOf(row.location)}
                      projectId={projectId}
                      location={row.location}
                      hit={row.hit}
                      showAllRequirements={requirementId === ALL_REQUIREMENTS}
                      focused={focusPlaceId === row.location.sourceId}
                      busy={busy}
                      shortlistRefusal={shortlistRefusal}
                      rejectRefusal={rejectRefusal}
                      onShortlist={onShortlist}
                      onReject={onReject}
                      onRestore={onRestore}
                      onAssignScenes={onAssignScenes}
                      onDetails={
                        projectId === undefined
                          ? undefined
                          : () => {
                              setDetailKey(keyOf(row.location));
                            }
                      }
                    />
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );

  return (
    <section data-testid="matched-locations" className="loc-page" style={{ gap: 14 }}>
      <div className="loc-results-head">
        <div>
          <h2 className="dh" data-testid="matched-headline">
            {view.locations.length} location{view.locations.length === 1 ? '' : 's'} matched
          </h2>
          <p className="loc-results-sub" data-testid="matched-coverage">
            {view.coveredRequirementCount} of {view.requirementCount} requirements covered
            {shortlisted.length > 0 ? ` · ${String(shortlisted.length)} shortlisted` : ''}
          </p>
          {/* HOW TO WORK THE LIST, once (owner's check of 2026-10-30): with no red
              button up here while every requirement has candidates, the rows'
              own Shortlist is the task — so the page says what it is for. */}
          <p className="loc-results-how" data-testid="matched-how">
            Open <strong>Photos &amp; details</strong> to look closer, then <strong>Shortlist</strong> the places worth a
            visit. Your team sees the shortlist under Shortlisted, and it is what you compare and export.
          </p>
        </div>
        {/* The pack carries the SHORTLIST, not everything that was found:
            forty machine suggestions sent to a host is noise, and the recipient
            cannot tell which ones anybody actually looked at. */}
        <div className="loc-toolbar-end">
          {headerPrimary === 'UNMATCHED' ? (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="matched-find-unmatched"
              onClick={() => {
                setFilter('UNMATCHED');
                setShowResults(true);
              }}
            >
              {`Find locations for ${String(view.unmatched.length)} unmatched`}
            </button>
          ) : null}
          <button
            type="button"
            className={headerPrimary === 'PACK' ? 'btn btn-primary' : 'ctl'}
            data-testid="export-location-pack"
            disabled={busy}
            onClick={() => {
              setPackOpen(true);
            }}
          >
            Export location pack
          </button>
        </div>
      </div>

      <SplitWorkspace
        testId="matched-workspace"
        navLabel="Requirements"
        backLabel="Requirements"
        showDetail={showResults}
        onBackToList={() => {
          setShowResults(false);
        }}
        nav={nav}
        detail={results}
      />

      {packOpen ? (
        <LocationPack
          projectTitle={projectTitle}
          scriptVersionLabel={scriptVersionLabel}
          locations={shortlisted}
          onClose={() => setPackOpen(false)}
          onPrint={onPackPrinted}
        />
      ) : null}

      {detail?.hit === undefined || projectId === undefined ? null : (
        <LocationDetailPanel
          projectId={projectId}
          location={detail.location}
          hit={detail.hit}
          busy={busy}
          shortlistRefusal={shortlistRefusal}
          rejectRefusal={rejectRefusal}
          onShortlist={() => {
            if (detail.hit !== undefined) onShortlist?.(detail.hit.candidateId, detail.hit.candidateVersion);
          }}
          onReject={(reason) => {
            if (detail.hit !== undefined) onReject?.(detail.hit.candidateId, detail.hit.candidateVersion, reason);
          }}
          onRestore={
            onRestore === undefined
              ? undefined
              : () => {
                  if (detail.hit !== undefined) onRestore(detail.hit.candidateId, detail.hit.candidateVersion);
                }
          }
          onClose={() => {
            setDetailKey(null);
          }}
        />
      )}
    </section>
  );
}

/**
 * "scenes 4, 12" when every scene carries its number; otherwise "2 scenes" —
 * a row of dashes says nothing about which scenes they are.
 */
function scenesLine(hit: MatchedRequirement): string {
  const n = hit.scenes.length;
  if (n === 0) return '';
  const numbers = hit.scenes.map((s) => s.sceneNumber).filter((x): x is string => x !== null && x !== '');
  if (numbers.length === n) return `${n === 1 ? 'scene' : 'scenes'} ${numbers.join(', ')}`;

  return `${String(n)} ${n === 1 ? 'scene' : 'scenes'}`;
}

function keyOf(location: MatchedLocation): string {
  return `${location.sourceType}:${String(location.sourceId)}`;
}

/**
 * The requirements the matched view knows, for the navigator: every one with a
 * candidate, in the order the matches first name them, then every approved one
 * with none. The figure beside each is a COUNT OF PLACES — how many locations
 * are candidates for it — never anything computed from their verdicts.
 */
export function requirementsOf(
  view: MatchedView,
): { id: string; title: string; locations: number; shortlisted: number }[] {
  const byId = new Map<string, { id: string; title: string; locations: number; shortlisted: number }>();
  for (const location of view.locations) {
    for (const r of location.requirements) {
      const entry = byId.get(r.requirementId) ?? { id: r.requirementId, title: r.title, locations: 0, shortlisted: 0 };
      entry.locations += 1;
      if (r.candidateStatus === 'SHORTLIST') entry.shortlisted += 1;
      byId.set(r.requirementId, entry);
    }
  }
  for (const u of view.unmatched) {
    if (!byId.has(u.id)) byId.set(u.id, { id: u.id, title: u.title, locations: 0, shortlisted: 0 });
  }
  return [...byId.values()];
}

/** The Locations tab's row: the shared result row with its own actions and test ids. */
function MatchedRow({
  projectId,
  location,
  hit,
  showAllRequirements,
  focused,
  busy,
  shortlistRefusal,
  rejectRefusal,
  onShortlist,
  onReject,
  onRestore,
  onAssignScenes,
  onDetails,
}: {
  projectId: string | undefined;
  location: MatchedLocation;
  hit: MatchedRequirement | undefined;
  showAllRequirements: boolean;
  focused: boolean;
  busy: boolean;
  shortlistRefusal: string | null;
  rejectRefusal: string | null;
  onShortlist?: (candidateId: string, version: number) => void;
  onReject?: (candidateId: string, version: number, reason: string) => void;
  /** A rejected place back to the candidates (REJECTED → LONGLIST). */
  onRestore?: (candidateId: string, version: number) => void;
  onAssignScenes?: (location: MatchedLocation) => void;
  onDetails?: () => void;
}) {
  const shortlisted = showAllRequirements
    ? location.requirements.some((r) => r.candidateStatus === 'SHORTLIST')
    : hit?.candidateStatus === 'SHORTLIST';
  const isRejected = hit?.candidateStatus === 'REJECTED';
  const id = String(location.sourceId);
  // A place serving several script locations is shortlisted for ONE of them —
  // the requirement on the left, or with all of them listed, its first. Say which.
  const forWhich = hit !== undefined && location.requirements.length > 1 ? hit.title : null;

  return (
    <LocationResultRow
      testId={`matched-card-${location.sourceType}-${id}`}
      photoPrefix="matched"
      location={location}
      hit={hit}
      hits={showAllRequirements ? location.requirements : hit === undefined ? [] : [hit]}
      shortlisted={shortlisted}
      focused={focused}
      footnote={
        shortlistRefusal === null ? null : (
          <p className="mm" data-testid={`shortlist-refusal-${id}`} style={{ fontSize: 11, margin: 0 }}>
            {shortlistRefusal}
          </p>
        )
      }
      actions={
        <>
          {isRejected ? (
            // Ruled out: the way back is to the candidates, never straight to
            // the shortlist (the API allows REJECTED → LONGLIST only). Restore
            // is a LONGLIST write, which the server gates on CANDIDATE_EDIT,
            // not on CANDIDATE_REJECT.
            <button
              type="button"
              className="ctl"
              data-testid={`restore-${id}`}
              disabled={busy || hit === undefined || shortlistRefusal !== null}
              title={shortlistRefusal ?? 'Puts this place back among the candidates'}
              onClick={() => {
                if (hit !== undefined) onRestore?.(hit.candidateId, hit.candidateVersion);
              }}
            >
              Restore
            </button>
          ) : (
            <>
              <button
                type="button"
                className="ctl"
                data-testid={`shortlist-${id}`}
                disabled={busy || hit === undefined || shortlisted || shortlistRefusal !== null}
                title={
                  shortlistRefusal ??
                  (hit === undefined ? undefined : `Adds this place to the shortlist for ${hit.title}`)
                }
                onClick={() => {
                  if (hit !== undefined) onShortlist?.(hit.candidateId, hit.candidateVersion);
                }}
              >
                {shortlisted ? 'Shortlisted ✓' : forWhich === null ? 'Shortlist' : `Shortlist for ${forWhich}`}
              </button>
              <RejectWithReason
                testId={`reject-${id}`}
                disabled={busy || hit === undefined || rejectRefusal !== null}
                title={rejectRefusal ?? undefined}
                onReject={(reason) => {
                  if (hit !== undefined) onReject?.(hit.candidateId, hit.candidateVersion, reason);
                }}
              />
            </>
          )}
          {onDetails === undefined ? null : (
            <button type="button" className="ctl" data-testid={`details-${id}`} onClick={onDetails}>
              {location.sourceType === 'LOCATION' ? 'Photos & details' : 'Details'}
            </button>
          )}
          {onAssignScenes === undefined ? null : (
            <button
              type="button"
              className="ctl"
              data-testid={`assign-scenes-${id}`}
              disabled={busy}
              onClick={() => {
                onAssignScenes(location);
              }}
            >
              Assign scenes
            </button>
          )}
          {projectId === undefined ? null : (
            <ConversationEntry projectId={projectId} anchorType="PLACE" anchorId={id} label={location.title} />
          )}
        </>
      }
    />
  );
}

/**
 * ONE MATCHED PLACE, COMPACTLY — the row both the Locations tab and the
 * Explore step draw (2026-10-30), so the two show the same record the same way.
 *
 * Level 1 is the picture, the name, where it is, the price, coverage and the
 * five axis states as marks; level 3 — the reasoning and the scenes with their
 * script — is one click away under "Why it matches". Each surface passes its
 * own actions and test ids.
 */
export function LocationResultRow({
  testId,
  photoPrefix,
  location,
  hit,
  hits,
  shortlisted,
  focused = false,
  status,
  actions,
  footnote = null,
  badge = null,
}: {
  testId: string;
  photoPrefix: string;
  location: MatchedLocation;
  /** The requirement this row is shown against: its coverage and its axes. */
  hit: MatchedRequirement | undefined;
  /** The requirements whose reasoning and scenes the disclosure carries. */
  hits: readonly MatchedRequirement[];
  shortlisted: boolean;
  focused?: boolean;
  status?: string;
  actions: ReactNode;
  footnote?: ReactNode;
  badge?: ReactNode;
}) {
  const ref = useScrollIntoViewWhen<HTMLLIElement>(focused);
  const scenes = hits.flatMap((r) => r.scenes.map((s) => ({ requirement: r, scene: s })));

  return (
    <li
      ref={ref}
      className="loc-row"
      data-testid={testId}
      data-status={status ?? hit?.candidateStatus}
      data-focused={focused ? '1' : undefined}
      style={focused ? POINTED_AT_STYLE : undefined}
    >
      <div className="loc-row-thumb">
        {location.imageUrl === null ? (
          <span className="loc-nophoto">
            <Icon name="pin" size={14} />
            {location.sourceType === 'LOCATION' ? 'No photo' : 'Scout find'}
          </span>
        ) : (
          <LocationPhoto location={location} shortlisted={shortlisted} variant="row" testIdPrefix={photoPrefix} />
        )}
      </div>

      <div className="loc-row-main">
        <div className="loc-row-top">
          <div className="loc-row-title">
            <h3>{location.title}</h3>
            <p className="loc-row-meta">{locationMetaLine(location)}</p>
            {badge}
          </div>
          {hit === undefined ? null : <Coverage requirement={hit} />}
        </div>

        {hit === undefined ? null : (
          <p className="loc-row-for">
            For <strong>{hit.title}</strong>
            {scenesLine(hit) === '' ? '' : ` · ${scenesLine(hit)}`}
          </p>
        )}

        {hit === undefined ? null : <AxisStrip requirement={hit} />}

        {hits.length === 0 ? null : (
          <Disclosure
            summary="Why it matches"
            meta={scenes.length === 0 ? undefined : `${String(scenes.length)} ${scenes.length === 1 ? 'scene' : 'scenes'}`}
            testId={`why-${testId}`}
          >
            {hits.map((r) => (
              <WhyItMatches key={r.candidateId} requirement={r} />
            ))}
            {scenes.length === 0 ? null : (
              <ul className="loc-scenes">
                {scenes.map(({ requirement, scene }) => (
                  <li key={`${requirement.candidateId}-${scene.sceneId}`} className="loc-scene" data-testid={`matched-scene-${scene.sceneId}`}>
                    <p className="loc-scene-slug">
                      <strong>{scene.sceneNumber ?? '—'}</strong> {scene.slugline ?? 'Untitled scene'}
                    </p>
                    {scene.excerpt === null ? null : (
                      <p className="loc-scene-excerpt">
                        “{scene.excerpt.slice(0, 220)}
                        {scene.excerpt.length > 220 ? '…' : ''}”
                      </p>
                    )}
                    <p style={{ marginTop: 4 }}>
                      <span className="mm">Covers:</span> {requirement.title}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Disclosure>
        )}

        <div className="loc-row-actions">{actions}</div>
        {footnote}
      </div>
    </li>
  );
}

/**
 * The number, and what it is a number OF.
 *
 * Labelled coverage every time it appears. An unlabelled percentage on a card
 * beside a photograph reads as a fit score, which is the one thing it is not.
 */
export function Coverage({ requirement }: { requirement: MatchedRequirement }) {
  if (!requirement.assessed) {
    return (
      <span className="stag" data-testid="coverage-not-assessed" style={{ marginLeft: 'auto', flex: 'none' }}>
        NOT ASSESSED
      </span>
    );
  }

  if (requirement.coverage.total === 0) {
    return (
      <span className="mm" data-testid="coverage-nothing-asked" style={{ marginLeft: 'auto', fontSize: 12, flex: 'none' }}>
        No must-haves set
      </span>
    );
  }

  const pct = Math.round((requirement.coverage.verified / requirement.coverage.total) * 100);

  return (
    <span style={{ marginLeft: 'auto', textAlign: 'right', flex: 'none' }} data-testid="coverage">
      <strong style={{ fontSize: 22, fontFamily: 'var(--font-heading)' }}>{pct}%</strong>
      <span className="mm" style={{ display: 'block', fontSize: 11 }}>
        {requirement.coverage.verified} of {requirement.coverage.total} must-haves covered
      </span>
    </span>
  );
}

const MARK: Record<string, { glyph: string; colour: string; tone: 'ok' | 'warn' | 'no'; word: string }> = {
  STRONG: { glyph: '✓', colour: 'var(--ok)', tone: 'ok', word: 'strong' },
  VERIFIED: { glyph: '✓', colour: 'var(--ok)', tone: 'ok', word: 'evidenced' },
  PARTIAL: { glyph: '△', colour: 'var(--warn)', tone: 'warn', word: 'partly' },
  UNKNOWN: { glyph: '△', colour: 'var(--warn)', tone: 'warn', word: 'not known' },
  WEAK: { glyph: '✕', colour: 'var(--danger)', tone: 'no', word: 'weak' },
  FAILS: { glyph: '✕', colour: 'var(--danger)', tone: 'no', word: 'not met' },
};

/**
 * THE FIVE AXES AT A GLANCE — a mark and a word each, the reasoning as the
 * mark's title. Five separate states side by side, never one figure over them:
 * nothing here counts, sums or averages them (`matching-surface.test.ts`).
 */
export function AxisStrip({ requirement }: { requirement: MatchedRequirement }) {
  if (!requirement.assessed) return null;

  return (
    <ul className="loc-axes" aria-label={`How it meets ${requirement.title}`}>
      {AXIS_ORDER.map((axis) => {
        const verdict = requirement.axes[axis];
        if (verdict === undefined) return null;
        const mark = MARK[verdict.state];

        return (
          <li
            key={axis}
            className="loc-axis"
            data-mark={mark?.tone}
            data-testid={`axis-chip-${axis}`}
            title={verdict.reasoning}
            aria-label={`${AXIS_LABEL[axis]}: ${mark?.word ?? verdict.state.toLowerCase()}`}
          >
            <span aria-hidden="true">{mark?.glyph ?? '·'}</span>
            <span aria-hidden="true">{AXIS_LABEL[axis]}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function WhyItMatches({ requirement }: { requirement: MatchedRequirement }) {
  if (!requirement.assessed) {
    return (
      <p className="mm" style={{ fontSize: 12, marginTop: 8 }}>
        Added by hand — not assessed against {requirement.title} yet.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <p className="flbl" style={{ margin: '0 0 6px' }}>
        Why it matches — {requirement.title}
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {AXIS_ORDER.map((axis) => {
          const verdict = requirement.axes[axis];
          if (verdict === undefined) return null;
          const mark = MARK[verdict.state] ?? { glyph: '·', colour: 'var(--color-neutral-500)' };

          return (
            <li
              key={axis}
              data-testid={`axis-${axis}`}
              style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}
            >
              <span aria-hidden="true" style={{ color: mark.colour, fontWeight: 700 }}>
                {mark.glyph}
              </span>
              {/* The reasoning is the point. A state with no sentence beside it
                  is the unexplained rating this product promises never to show. */}
              <span>{verdict.reasoning}</span>
              {verdict.hardCapped ? (
                <span className="mm" style={{ fontSize: 11 }}>
                  · capped
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MergePanel({
  suggestions,
  busy,
  onMerge,
}: {
  suggestions: readonly MergeSuggestion[];
  busy: boolean;
  onMerge?: (ids: readonly string[]) => void;
}) {
  if (suggestions.length === 0) {
    return (
      <EmptyState testId="no-merge-suggestions" title="No requirements look like duplicates." />
    );
  }

  return (
    <div data-testid="merge-suggestions" style={{ display: 'grid', gap: 10 }}>
      {suggestions.map((s) => (
        <div key={s.requirementIds.join('-')} className="ui-panel" style={{ padding: '12px 14px' }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            <strong>{s.titles.join('” and “').replace(/^/, '“').concat('”')}</strong> may be one
            place.
          </p>
          <p className="mm" style={{ margin: '4px 0 0', fontSize: 12 }}>
            {s.reason}
            {s.scenes.length > 0 ? ` · scenes ${s.scenes.join(', ')}` : ''}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {/* Neutral: a list of these is several decisions, none of them the page's one. */}
            <button
              type="button"
              className="ctl"
              data-testid={`merge-${s.requirementIds.join('-')}`}
              disabled={busy}
              onClick={() => onMerge?.(s.requirementIds)}
            >
              Merge into one requirement
            </button>
            <button type="button" className="ctl" disabled={busy}>
              Keep separate
            </button>
          </div>
          {/* The design's own line, and the rule worth keeping. */}
          <p className="mm" style={{ margin: '8px 0 0', fontSize: 11 }}>
            Either way the call is logged with your name — nothing is merged for you.
          </p>
        </div>
      ))}
    </div>
  );
}

function UnmatchedPanel({
  unmatched,
  selected,
  busy,
  primary,
  note,
  onNote,
  onToggle,
  onRequest,
}: {
  unmatched: MatchedView['unmatched'];
  selected: readonly string[];
  busy: boolean;
  primary: boolean;
  note: string;
  onNote: (value: string) => void;
  onToggle: (id: string) => void;
  onRequest: () => void;
}) {
  if (unmatched.length === 0) {
    return <EmptyState testId="nothing-unmatched" title="Every requirement has at least one candidate." />;
  }

  return (
    <div className="split-2" data-testid="unmatched-panel" style={{ gap: 20, alignItems: 'start' }}>
      <div style={{ minWidth: 0 }}>
        <h3 className="dh" style={{ fontSize: 22, margin: 0 }}>
          {unmatched.length} requirement{unmatched.length === 1 ? '' : 's'} still need locations
        </h3>
        <p className="mm" style={{ fontSize: 12.5, margin: '6px 0 12px' }}>
          Select the ones you want ScoutAround to find — the brief is pre-filled from your script
          analysis.
        </p>

        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {unmatched.map((u) => {
            const picked = selected.includes(u.id);
            const scenes = u.scenes ?? [];

            return (
              <li
                key={u.id}
                data-testid={`unmatched-row-${u.id}`}
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-divider)',
                  // A rule down the side rather than a fill: a selected row
                  // still has to be readable, and this list is a brief somebody
                  // proof-reads before sending it to a coordinator.
                  borderLeft: picked ? '3px solid var(--color-accent)' : '1px solid var(--color-divider)',
                  padding: '10px 12px',
                }}
              >
                <label style={{ display: 'flex', gap: 10, alignItems: 'baseline', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={picked}
                    disabled={busy}
                    onChange={() => {
                      onToggle(u.id);
                    }}
                    data-testid={`unmatched-${u.id}`}
                  />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 13.5 }}>{u.title}</span>
                      {scenes.length === 0 ? null : (
                        <span className="mm" style={{ fontSize: 12, marginLeft: 'auto' }}>
                          Scenes {scenes.join(', ')}
                        </span>
                      )}
                    </span>

                    {u.description ? (
                      <span
                        className="mm"
                        data-testid={`unmatched-brief-${u.id}`}
                        style={{ display: 'block', fontSize: 12.5, marginTop: 6, lineHeight: 1.55 }}
                      >
                        {u.description}
                      </span>
                    ) : null}

                    {u.target ? (
                      <span
                        className="mm"
                        data-testid={`unmatched-target-${u.id}`}
                        style={{ display: 'block', fontSize: 12, marginTop: 6 }}
                      >
                        Target: <strong>{u.target}</strong>
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* THE REQUEST SITS BESIDE THE BRIEF IT IS MADE FROM. It used to be a
          button under the list, which put the count of what was selected and
          the thing being sent at opposite ends of a scroll. */}
      <aside
        data-testid="scouting-request"
        style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-divider)',
          borderLeft: '3px solid var(--color-accent)',
          padding: 16,
          minWidth: 0,
        }}
      >
        <p className="flbl" style={{ margin: 0, letterSpacing: '0.08em', color: 'var(--color-accent)' }}>
          Scouting request
        </p>
        <p className="dh" data-testid="scouting-count" style={{ fontSize: 22, margin: '8px 0 0' }}>
          {selected.length} selected
        </p>
        <p className="mm" style={{ fontSize: 12.5, margin: '10px 0 0', lineHeight: 1.6 }}>
          A ScoutAround coordinator reviews the brief, finds candidates and replies inside this
          project.
        </p>

        <label style={{ display: 'block', marginTop: 14 }}>
          <span className="flbl">Notes for the coordinator</span>
          <textarea
            data-testid="scouting-note"
            value={note}
            rows={4}
            disabled={busy}
            placeholder="Anything the script doesn&apos;t say — access, period accuracy, budget ceilings…"
            onChange={(e) => {
              onNote(e.target.value);
            }}
            style={{
              width: '100%',
              marginTop: 6,
              padding: 10,
              fontSize: 12.5,
              border: '1px solid var(--color-divider)',
              fontFamily: 'inherit',
            }}
          />
        </label>

        <button
          type="button"
          className={primary ? 'btn btn-primary' : 'btn btn-ghost'}
          data-testid="request-scouting"
          disabled={busy || selected.length === 0}
          onClick={onRequest}
          style={{ width: '100%', marginTop: 12 }}
        >
          Request ScoutAround to find these
        </button>

        <p className="mm" style={{ fontSize: 11, margin: '10px 0 0', lineHeight: 1.6 }}>
          {/* The design also promised "typically within 2 business days". That
              is an operational commitment nothing in this service measures or
              enforces, so it is not written; the no-cost-until-approved half IS
              how the requests module works. */}
          Connected to Production Concierge — no cost until you approve a proposal.
        </p>
      </aside>
    </div>
  );
}
